import { alpha } from '@mui/material/styles';

// ==============================|| PALETTE ||============================== //


// SETUP COLORS
const COMMON = {
  black: '#000',
  white: '#fff',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB', // #F9FAFB #F1F5F9
  200: '#DDDDDD', // #F4F6F8
  300: '#F5F5F5', // #DFE3E8
  400: '#CCCCCC', // #C4CDD5
  500: '#8E8E8E',  // #8E8E8E #919EAB
  600: '#727272', // #727272 #637381
  700: '#454F5B',
  800: '#333333', // 212B36
  900: '#161C24',
};

const WHITE = {
  5:  alpha(COMMON.white, 0.02),
  10: alpha(COMMON.white, 0.1),
  30: alpha(COMMON.white, 0.3),
  50: alpha(COMMON.white, 0.5),
  70: alpha(COMMON.white, 0.7),
  100: COMMON.white
};

const BLACK = {
  5: alpha(COMMON.black, 0.02),
  10: alpha(COMMON.black, 0.1),
  30: alpha(COMMON.black, 0.3),
  50: alpha(COMMON.black, 0.5),
  70: alpha(COMMON.black, 0.7),
  100: COMMON.black
};

const COLOR = {
  primaryMain: '#3260C9',
  primaryBlue1: '#112C7E',
  primaryBlue2: '#2655E7',
  primaryBlue270: alpha('#2655E7', 0.7),
  primaryBlue3: '#1C92FF',
  primaryBlue310: alpha('#2898FF', 0.1),
  primaryBlue370: alpha('#2898FF', 0.7),
  primaryBlue4: '#052169',
  primaryBlue5: '#16369D',
  primaryBlue6: '#0024B2',
  primarySelectBackground: '#D3E4FF',
  primaryHoverBackground: '#EBF4FF',
  primaryClick: '#1842A2',
  
  secondaryGreen1: '#B8FD74',
  secondaryGreen2: alpha('#93EB3D', 0.65),
  secondaryGreen3: '#8EE33B',
  secondaryLighter: '#A5D6FF', // #A5D6FF
  secondaryLighter32: alpha('#A5D6FF', 0.32),
  secondaryLight: '#437CFF', // #437CFF
  secondaryDark: '#17417E', // #17417E
  secondaryMain: '#324CBB', // #324CBB

  successMain: '#94D7A0',
  successHoverBackground: '#EBF8EE',

  warningMain: '#FFDF84',
  warningHoverBackground: '#FFF5E9',

  errorMain: '#FF9599',
  errorHoverBackground: '#FFEBEC',

  backgroundBorder: '#D8D8D8',
  background: '#000E46',
  backgroundBackdrop: alpha(GREY[800], 0.7),
};

const PRIMARY = {
  lighter: '#F5F6FF', // #D1E9FC
  light: '#A5D6FF', // #324CBB
  main: '#2655E7',  // #011E4D
  dark: '#2655E7',  // #103996
  darker: '#112C7E', // #061B64
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#E6F4FF',  //
  light: '#B8FD74',  // #84A9FF
  main: '#B8FD74', // #A7CBE0
  dark: '#17417E', //
  darker: '#16369D',  // 091A7A
  contrastText: '#fff',
};

const INFO = {
  lighter: '#D0F2FF',
  light: '#1C92FF', // #74CAFF
  main: '#16369D',  // #3260C9
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#EBF8EE',
  light: '#AAF27F',
  main: '#3ABC5D',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: GREY[800],
};

const WARNING = {
  lighter: '#FFF5E9',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFEBEC', // #FFE7D9
  light: '#FFA48D',
  main: '#F23C47', // #EE3A43
  dark: '#B72136',  //
  darker: '#7A0C2E',
  contrastText: '#fff',
};

const palette = {
  common: COMMON,
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: GREY[400], //
  text: {
    primary: GREY[800],
    secondary: GREY[500],
    disabled: GREY[500],
    link: COMMON.white,
  },
  action: {
    active: GREY[500], //
    hover: '#F5F6FF',
    selected: '#489CFF', //
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
  gradients: {
    header: 'linear-gradient(180deg, #122265 0%, rgba(18, 34, 101, 0.60) 154.17%)',
    header2: 'linear-gradient(180deg, #122265 0%, rgba(18, 34, 101, 0.60) 154.17%)',
    header1: 'linear-gradient(180deg, #0024B2 0%, rgba(0, 42, 207, 0.00) 100%)',

    banner1: 'linear-gradient(0deg, rgba(0, 14, 70, 0.00) 0%, #000E46 100%)',
    banner2: 'linear-gradient(360deg, #000E46 0%, rgba(4, 23, 58, 0.7) 20.67%, rgba(4, 23, 58, 0) 55.62%)',

    blue1: 'linear-gradient(360deg, #04173A 0%, rgba(4, 23, 58, 0.7) 20.67%, rgba(4, 23, 58, 0) 55.62%)',
    blue2: 'linear-gradient(297.59deg, #3260C9 9.27%, #1646B5 72.25%)',
    blue3: 'linear-gradient(104deg, #3B48F5 5.79%, #6687F7 100%)',
    blue4: 'linear-gradient(180deg, #000E46 0%, rgba(0, 14, 70, 0.00) 100%)',
    blue5: 'linear-gradient(0deg, rgba(4, 23, 58, 0.40) 0%, rgba(4, 23, 58, 0.00) 22%)',
    blue6: 'linear-gradient(270deg, rgba(0, 0, 0, 0.10) 0%, rgba(251, 251, 251, 0.00) 100%)',

    orange1: 'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)',
    purple1: 'linear-gradient(135deg, #43CBFF 0%, #9708CC 100%)',
    pink1: 'linear-gradient(135deg, #F6CEEC 0%, #D939CD 100%)',
    green1: 'linear-gradient(135deg, #FFF720 0%, #3CD500 100%)',
    black1: 'linear-gradient(100.66deg, #434343 6.56%, #000000 93.57%)',
    gradient1: 'linear-gradient(rgb(255, 255, 255) 0%, rgb(239, 241, 255) 100%)',
  },
  color: COLOR,
  icon: {
    primaryArrow: '#B8FD74',
    disabledArrow: '#16369D',
  },
  toast: {
    background: '#2655E7',
    contrastText: COMMON.white,
  },
  white: WHITE,
  black: BLACK,
  mode: 'dark',
  background: {
    paper: '#fff', // #0F203A
    page: PRIMARY.lighter, // #001432
    default: '#000E46', // 011E4D
    disabled: '#F2F3F7',
    neutral: GREY[500],
    complementary: GREY[10],
  },
  // tonalOffset: 0.5
}

export default palette;
